import React from 'react'
import HelpBox from './HelpBox'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'
import { sridName } from '../../constants/srids'
import {
	alternativaId,
	plats,
	markering,
	historik,
	lägesosäkerhetPlan,
	mbHojd,
	lägesosäkerhetHöjd,
	planKoordinaterString,
	höjdKoordinatString,
	firstBild,
	anmärkning,
	äldreHöjdsystem
} from '../../selectors/stompunkt'

const styles = (theme) => ({
	table: {
		width: '100%',
		borderCollapse: 'collapse',
		'& td': {
			borderBottom: '1px solid ' + theme.palette.grey[300],
			paddingTop: theme.spacing.unit * 1.5,
			paddingBottom: theme.spacing.unit * 1.5,
			paddingLeft: 0,
			paddingRight: 0,
			verticalAlign: 'text-top',
		}
	},
	tableLabelCell: {
		width: '135px',
	},
	valueText: {
		whiteSpace: 'pre-line',
		position: 'relative',
	},
	propertyGroup: {
		minHeight: 1, //IE11 flexbox bug fix
		flexShrink: 0, //IE11 flexbox bug fix
		marginBottom: theme.spacing.unit * 3,
	},
	punktskiss: {
		maxWidth: '100%',
	},
})

export default ({ stompunkt, srid }) => (
	<React.Fragment>
		<PropertyGroup heading="Allmänt">
			<PropertyTable properties={[
				{ name: "Namn", value: stompunkt.properties.namn },
				{ name: "Stompunkt ID", value: stompunkt.properties.stompunktId },
				{ name: "Alternativa ID", value: alternativaId(stompunkt) },
				{ name: "Stomnät", value: stompunkt.properties.stomnat.natNamn },
				{ name: "Typ", value: capitalize(stompunkt.properties.typ) },
				{ name: "Kategori", value: capitalize(stompunkt.properties.kategori) },
				{ name: "Kommun", value: plats(stompunkt) },
				{ name: "Markering", value: capitalize(markering(stompunkt)) },
				{ name: "Historik", value: historik(stompunkt) }
			]}/>
		</PropertyGroup>
		<PropertyGroup heading="Plan">
			<PropertyTable properties={[
				{ name: sridName(srid), value: planKoordinaterString(stompunkt, srid) },
				{ name: "Ursprung", value: capitalize(stompunkt.properties.mbPlan.ursprung) },
				{ name: "Mätmetod", value: capitalize(stompunkt.properties.mbPlan.matmetod) },
				{ name: "Mätningsdatum", value: stompunkt.properties.mbPlan.matningDatum },
				{ name: "Beräkningsdatum", value: stompunkt.properties.mbPlan.berakningDatum },
				{ name: "Kvalitetsklass", value: stompunkt.properties.mbPlan.kvalitetKlass, help: "1: SWEREF-punkter\n2: RIX 95-punkter",tabindex:0 },
				{ name: "Lägesosäkerhet", value: lägesosäkerhetPlan(stompunkt), help: "Utvidgad mätosäkerhet (2σ = 98%)",tabindex:0 },
			]}/>
		</PropertyGroup>
		<PropertyGroup heading="Höjd">
			<PropertyTable properties={[
				{ name: "RH 2000", value: höjdKoordinatString(stompunkt) },
				{ name: "Ursprung", value: capitalize(mbHojd(stompunkt).ursprung) },
				{ name: "Mätmetod", value: capitalize(mbHojd(stompunkt).matmetod) },
				{ name: "Mätningsdatum", value: mbHojd(stompunkt).matningDatum },
				{ name: "Beräkningsdatum", value: mbHojd(stompunkt).berakningDatum },
				{ name: "Kvalitetsklass", value: mbHojd(stompunkt).kvalitetKlass, help: "0: Definierar höjdsystemet\n1: Finavvägd\n2: Utjämnad med GNSS",tabindex:0 },
				{ name: "Lägesosäkerhet", value: lägesosäkerhetHöjd(stompunkt), help: "Utvidgad mätosäkerhet (2σ = 95%)",tabindex:0 },
				{ name: "Äldre höjdsystem", value: äldreHöjdsystem(stompunkt), help: "Osäkerheten för höjder i RH 00 och RH 70\n varierar mellan mm- och dm-nivå, oavsett\n antal decimaler som visas.",tabindex:0 }
			]}/>
		</PropertyGroup>
		<PropertyGroup heading="Punktskiss">
			<Punktskiss src={firstBild(stompunkt)} />
		</PropertyGroup>
		<PropertyLongText heading="Lägesbeskrivning" text={anmärkning(stompunkt, "lägesbeskrivning")} />
		<PropertyLongText heading="Markhöjd" text={anmärkning(stompunkt, "markhöjd")} />
		<PropertyLongText heading="Distansbricka" text={anmärkning(stompunkt, "distansbricka")} />
		<PropertyLongText heading="Excentriska markeringar" text={anmärkning(stompunkt, "excentriska markeringar")} />
		<PropertyLongText heading="Övrigt" text={anmärkning(stompunkt, "övrigt")} />
	</React.Fragment>
)

const PropertyGroup = withStyles(styles)(({ heading, children, classes }) => {
	if (children) {
		return (
			<div className={classes.propertyGroup}>
				<Typography variant="subheading">
					{heading}
				</Typography>
				{children}
			</div>
		)
	}
	else {
		return null
	}
})

const PropertyTable = withStyles(styles)(({ properties, classes }) => (
	<table className={classes.table}>
		<tbody>
			{ properties.map((props) => <PropertyTableRow {...props} key={props.name}/>) }
		</tbody>
	</table>
))

const PropertyTableRow = withStyles(styles)(({ name, value, help, classes }) => {
	return value ? (
		<tr className={classes.tableRow}>
			<td className={classes.tableLabelCell}>
				<Typography variant="body2">
					{name}
				</Typography>
			</td>
			<td>
				<Typography variant="body1" className={classes.valueText}>
					{value}
					<HelpBox text={help} />
				</Typography>
			</td>
		</tr>
	) : null
})

const PropertyLongText = withStyles(styles)(({ heading, text, classes }) => {
	return text && text.length
		? (
			<PropertyGroup heading={heading}>
				<Typography variant="body1" className={classes.valueText}>
					{text}
				</Typography>
			</PropertyGroup>
		)
		: null
})

const Punktskiss = withStyles(styles)(({ src, classes }) => {
	return src
		? <img src={src} alt="Punktskiss" className={classes.punktskiss} />
		: null
})

function capitalize(string) {
    return string
    	? string.charAt(0).toUpperCase() + string.slice(1)
    	: undefined
}


// WEBPACK FOOTER //
// ./src/components/Stompunkt/StompunktSuccess.js